import { parseLocale } from 'lib/constants/localization'
import _useTranslation from 'lib/hooks/_useTranslation'
import { GetServerSideProps } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import styled from 'styled-components'
import { Know } from 'interfaces/Know'
import Meta from 'components/modules/SEO/Meta'
import HomepageKnows from 'components/modules/LandingPage/Homepage/HomepageKnows'
import HomepageMainFeatures from 'components/modules/LandingPage/Homepage/HomepageMainFeatures'
import HomepageHeader from 'components/modules/LandingPage/Homepage/HomepageHeader'
import ScaledWrapper from 'components/layout/ScaledWrapper'
import { HomepageSubject, returnSubjects } from 'lib/constants/homepage'
import KnowService from 'services/KnowService'
import dynamic from 'next/dynamic'
import HomepageChat from 'components/modules/LandingPage/Homepage/HomepageChat'
import HomepageAICompanion from 'components/modules/LandingPage/Homepage/HomepageAICompanion'
import { isDeviceMobile } from 'lib/features/deviceInfo'
import { redirectToAppDownloadUrlAsync } from 'lib/features/app-links'

const HomepageFeatures = dynamic(() => import('components/modules/LandingPage/Homepage/HomepageFeatures'), { ssr: true })
const DownloadQRElement = dynamic(() => import('components/modules/LandingPage/Homepage/DownloadQRElement'), { ssr: true })
const HomepageReviews = dynamic(() => import('components/modules/LandingPage/Homepage/HomepageReviews'), { ssr: true })
const FAQSection = dynamic(() => import('stories/V1/sections/FAQSection'), { ssr: true })

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
    overflow-x: hidden;
    background-color: #0d0d0d;

    @media (max-width: 860px) {
        gap: 8px;
    }
`

const Content = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
    overflow-x: hidden;
`

interface Props {
    subjectKnows: Record<string, Know[]>
    subjects: HomepageSubject[]
}

const Homepage = ({ subjectKnows, subjects }: Props) => {
    const { t } = _useTranslation('homepage')
    const isMobile = isDeviceMobile()

    const scrollToDownloadQR = (referrerScreen = 'header') => {
        if (isMobile) {
            redirectToAppDownloadUrlAsync('dynamic', referrerScreen)
            return
        }
        const downloadQR = document.getElementById('download-qr')
        if (downloadQR) {
            try {
                downloadQR.scrollIntoView({ behavior: 'smooth', block: 'center' })
            } catch (error) {
                redirectToAppDownloadUrlAsync('dynamic', referrerScreen)
            }
        } else {
            redirectToAppDownloadUrlAsync('dynamic', referrerScreen)
        }
    }

    const faqsData = [
        {
            question: t('homepage/aiCompanionFaqTitle'),
            answer: t('homepage/aiCompanionFaqSubtitle'),
        },
        {
            question: t('homepage/faqTitle1'),
            answer: t('homepage/faqSubtitle1'),
        },
        {
            question: t('homepage/faqTitle2'),
            answer: t('homepage/faqSubtitle2'),
        },
    ]

    return (
        <Container>
            <ScaledWrapper showBackground>
                <Content>
                    <Meta title={t('homepage/pageTitle')} description={t('homepage/pageDescription')} />
                    <HomepageHeader onButtonClick={scrollToDownloadQR} />
                    <HomepageChat />
                    <HomepageAICompanion />
                    <HomepageMainFeatures onButtonClick={scrollToDownloadQR} />
                    <HomepageFeatures onButtonClick={scrollToDownloadQR} />
                    {subjects.length > 0 && subjectKnows && (
                        <HomepageKnows subjectKnows={subjectKnows} subjects={subjects} />
                    )}
                    <DownloadQRElement />
                    <HomepageReviews />
                </Content>
            </ScaledWrapper>
            <FAQSection faqData={faqsData} title={t('homepage/faqSectionHeader')} />
        </Container>
    )
}

export default Homepage

export const getServerSideProps: GetServerSideProps = async (props) => {
    const parsedLocale = parseLocale(props.locale)
    const subjects = returnSubjects(parsedLocale.contentLanguageCode)

    // Redirect logic similar to middleware
    const host = props.req.headers.host
    const path = props.resolvedUrl

    if (host === 'knowunity.com' && path === '/') {
        return {
            redirect: {
                destination: `https://knowunity.ai${path}`,
                permanent: false,
            },
        }
    }

    const subjectKnows: Record<string, Know[]> = {}

    const subjectsByKey: Record<string, HomepageSubject> = {}
    subjects.forEach((subject) => {
        const key = subject.titleKey || subject.title || ''
        subjectsByKey[key] = subject
    })

    await Promise.all(
        subjects.map(async (subject) => {
            const knowPromises = subject.knowUuids.map((uuid) => KnowService.get(uuid).catch(() => null))
            const knows = await Promise.all(knowPromises)
            const key = subject.titleKey || subject.title || ''
            subjectKnows[key] = knows.filter((know): know is Know => know !== null)
        })
    )

    return {
        props: {
            subjectKnows,
            subjects,
            ...(await serverSideTranslations(parsedLocale.interfaceLanguageCode, ['common', 'homepage'])),
        },
    }
}
